<template>
  <div>
    <!-- Groups List -->
    <div v-for="item in groupData" :key="'group_' + item.id" class="node-hover">
      <div :style="{'margin-left': 25 * depth + 'px'}">
        <div class="d-flex justify-content-between">
          <div class="node" @click="toggleGroup(item)">
            <span>{{ expanded && expandedGroupId === item.id ? '&#9660' : '&#9658'}}</span>
            <span>{{ item.name }}</span>
          </div>
          <div class="d-flex">
            <div class="dropdown w-30">
              <button type="button" class="w-44 btn btn-sm dropdown-toggle py-0" data-bs-toggle="dropdown">
                <img src="/app-assets/images/icons/plus-solid.svg" alt="">
              </button>
              <div class="dropdown-menu dropdown-menu-end">
                <a @click.prevent="onClickAddGroupBtn(item)" v-if="depth < 3" class="dropdown-item">
                  <span>Add sub group</span>
                </a>
                <a @click.prevent="onClickAddAccount(item)" class="dropdown-item">
                  <span>Add account</span>
                </a>
              </div>
            </div>
            <div class="w-20 z-999"></div>
            <div>
              <img style="cursor:pointer"
                  @click="handleGroupEdit(item)"
                  width="13"
                  class="cursor-pointer"
                  src="/app-assets/images/icons/pencil-alt-solid.svg"
                  alt=""
              >
            </div>
          </div>
        </div>

        <!-- Recursive Children -->
        <div v-if="expanded && expandedGroupId === item.id">
          <div v-if="loading" :style="{'margin-left': 25 * (depth + 1) + 'px'}" class="spinner-border spinner-border-sm text-primary my-1" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <TreeBrowser
              :key="'tree_' + item.id"
              :groupData="nestedData.group"
              :endpointData="nestedData.endpoint"
              :parent="item"
              :depth="depth + 1"
              :accountType="accountType"
              @paginationEmit="handlePaginationEmit"
              @onClickAddGroup="onClickAddGroupBtn"
              @onGroupUpdate="handleGroupEdit"
              @onClickAddAccount="onClickAddAccount"
              @onAccountUpdate="handleAccountEdit"
          />
        </div>
      </div>
    </div>

    <hr class="mb-1 mt-0" v-if="depth === 0 && groupData.length">

    <!-- Endpoints List (Paginated) -->
    <div v-if="endpointData.data && endpointData.data.length > 0" class="pt-2">
      <div v-for="item in endpointData.data" :key="'endpoint_' + item.id" :style="{'margin-left': 25 * depth + 'px'}">
        <div class="d-flex cursor-initial justify-content-between bg-black-light rounded-8 p-1 mb-1">
          <p class="mb-0">{{ item.name }}</p>
          <div class="d-flex">
            <p class="mb-0 me-1">{{ getBudgetAmount(item) }}</p>
            <img @click="handleAccountEdit(item)" width="13" class="cursor-pointer"
                 src="/app-assets/images/icons/pencil-alt-solid.svg" alt="">
          </div>
        </div>
      </div>

      <!-- Pagination for Endpoints -->
      <div v-if="endpointData.total > endpointData.per_page" :style="{'margin-left': 25 * depth + 'px'}" class="my-1 d-flex justify-content-end">
          <Pagination
            :stateName="paginationStateName"
            @onPageChange="onPageChange"
          />
      </div>
    </div>
  </div>
</template>

<script>
import handleProjects from "@/services/modules/procurement/project";
import Pagination from '@/components/atom/Pagination'

export default {
  name: "TreeBrowser",
  components: {
    Pagination
  },
  props: {
    groupData: {
      type: Array,
      default: () => []
    },
    endpointData: {
      type: Object,
      default: () => ({ data: [] })
    },
    depth: {
      type: Number,
      default: 0
    },
    parent: {
      type: Object,
      default: null
    },
    accountType: {
      type: String,
      default: 'item'
    }
  },
  data() {
    return {
      expanded: false,
      expandedGroupId: null,
      nestedData: {
        group: [],
        endpoint: {}
      },
      loading: false,
    }
  },
  computed: {
    paginationStateName() {
      // The pagination shown in this component instance belongs to the parent of the current list
      return `pagination_node_${this.parent ? this.parent.id : 'root'}`;
    }
  },
  methods: {
    async toggleGroup(group) {
      if (this.expanded && this.expandedGroupId === group.id) {
        this.expanded = false;
        this.expandedGroupId = null;
        return;
      }
      
      // Reset nested data for the new branch to avoid data ghosting/conflicts
      this.nestedData.group = [];
      this.nestedData.endpoint = { data: [] };
      
      this.expandedGroupId = group.id;
      this.expanded = true;
      await this.fetchChildren(group.id);
    },

    async fetchChildren(groupId, page = 1) {
      this.loading = true;
      try {
        const offset = 10;
        const group = this.groupData.find(g => g.id === groupId) || this.parent || {};
        const query = `?company_id=${this.$route.params.companyId}&parent_id=${groupId}&account_type=${group.project_account_type || ''}&page=${page}&offset=${offset}`;
        const res = await this.fetchAccountTree(this.$route.params.projectId, query);
        if (! res.status) {
          this.loading = false;
          return;
        }

        if (page == 1) {
          this.nestedData.group = res.data.group;
        }
        this.nestedData.endpoint = res.data.endpoint;
        this.setPagination(res.data.endpoint, groupId);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    setPagination(data, groupId) {
      this.$store.commit('setPaginationData', {
        data: {
          records: data.total,
          perPage: data.per_page,
          currentPage: data.current_page,
        },
        stateName: `pagination_node_${groupId}`
      });
    },

    onPageChange(page) {
      this.$emit('paginationEmit', page);
      
      let routeQuery = Object.assign({}, this.$route.query);
      routeQuery.page = page;
      this.$router.replace({ query: routeQuery }).catch(() => {});
    },

    handlePaginationEmit(page) {
      if (this.expandedGroupId) {
        this.fetchChildren(this.expandedGroupId, page);
      }
    },

    onClickAddGroupBtn(node) {
      this.$emit('onClickAddGroup', node)
    },
    onClickAddAccount(node) {
      this.$emit('onClickAddAccount', node)
    },
    handleGroupEdit(node) {
      this.$emit('onGroupUpdate', node)
    },
    handleAccountEdit(node) {
      this.$emit('onAccountUpdate', node)
    },
    getBudgetAmount(node) {
      if (!node.budget_general || node.budget_general.length < 1) {
        return '';
      }
      const bg = node.budget_general[0];
      return this.accountType === 'activity_target' ? bg.quantity : bg.amount;
    },
  },
  setup() {
    const { fetchAccountTree } = handleProjects();
    return { fetchAccountTree };
  }
}
</script>

<style scoped>
.bg-black-light {
  background: #00000024;
}

.rounded-8 {
  border-radius: 8px;
}

.node {
  cursor: pointer;
}

.node-hover {
  padding: 10px 2px;
}

.node-hover:hover {
  background: #fffeec;
}

.cursor-initial {
  cursor: initial;
}

.cursor-pointer {
  cursor: pointer;
}

.w-30 {
  width: 30px;
}

.w-20 {
  width: 20px;
}

.z-999 {
  z-index: 999;
}

.w-44 {
  width: 44px;
}
</style>